import { fetchAuthSession } from 'aws-amplify/auth';
import { normalizePublicUsername, usernameFromEmail } from './authUsername';

export class AccountsApiError extends Error {
  status?: number;
  body?: unknown;
  constructor(message: string, status?: number, body?: unknown) {
    super(message);
    this.name = 'AccountsApiError';
    this.status = status;
    this.body = body;
  }
}

function shouldUseDevAccountsProxy(baseUrl: string): boolean {
  if (process.env.NEXT_PUBLIC_USE_DEV_ACCOUNTS_PROXY !== '1') return false;

  // If we're running the app on localhost, proxy cross-origin API calls through Next rewrites
  // to avoid CORS and to surface real error bodies during development.
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  const isLocalhost = host === 'localhost' || host === '127.0.0.1';
  if (!isLocalhost) return false;
  if (baseUrl.startsWith('/')) return false;
  try {
    const u = new URL(baseUrl);
    return u.origin !== window.location.origin;
  } catch {
    return false;
  }
}

function normalizeAccountsBaseUrl(rawBaseUrl: string): string {
  const trimmed = (rawBaseUrl ?? '').trim().replace(/\/+$/, '');
  if (!trimmed) {
    throw new AccountsApiError('Invalid NEXT_PUBLIC_API_URL', 0);
  }

  // Support relative bases like "/api/v1/accounts" (same-origin proxying).
  if (trimmed.startsWith('/')) {
    return normalizeAccountsPath(trimmed);
  }

  try {
    const url = new URL(trimmed);
    const normalizedPath = normalizeAccountsPath(url.pathname);
    return url.origin + normalizedPath;
  } catch {
    // If it's not a valid URL, return as-is; better to fail with a network error than
    // to silently invent a base that could be wrong.
    return trimmed;
  }
}

function normalizeAccountsPath(pathname: string): string {
  let p = (pathname ?? '').trim();
  if (!p) p = '/';
  p = p.replace(/\/+$/, '');
  // If the input was "/" (or "///"), stripping trailing slashes yields "".
  // Treat that as the origin root so we can append the correct API prefix.
  if (p === '') p = '/';

  const lower = p.toLowerCase();

  // Already an accounts base (or the dev proxy base).
  if (lower.endsWith('/api/v1/accounts')) return p;
  if (lower.endsWith('/dev-accounts')) return p;
  if (lower.endsWith('/accounts')) return p;

  // Common "origin only" or "API root" values.
  if (p === '/') return '/api/v1/accounts';
  if (lower.endsWith('/api')) return p + '/v1/accounts';
  if (lower.endsWith('/api/v1')) return p + '/accounts';

  return p;
}

export async function apiFetchAccounts<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const rawBaseUrl = process.env.NEXT_PUBLIC_API_URL;
  if (!rawBaseUrl) {
    throw new AccountsApiError('Missing NEXT_PUBLIC_API_URL', 0);
  }
  let baseUrl = normalizeAccountsBaseUrl(rawBaseUrl);
  if (shouldUseDevAccountsProxy(baseUrl)) {
    baseUrl = '/dev-accounts';
  }

  const execute = async (sessionOverride?: Awaited<ReturnType<typeof fetchAuthSession>>): Promise<Response> => {
    let activeSession = sessionOverride;
    if (!activeSession) {
      try {
        activeSession = await fetchAuthSession();
      } catch {
        throw new AccountsApiError('Authentication session unavailable', 401);
      }
    }

    // Prefer ID tokens for API authorization.
    // The current backend JWT verifier expects an `aud` claim, which Cognito ID tokens have.
    // (Cognito access tokens often omit `aud` and use `client_id` instead.)
    const idToken = activeSession.tokens?.idToken?.toString();
    const accessToken = activeSession.tokens?.accessToken?.toString();
    const candidates = [idToken, accessToken].filter((t, i, arr) => Boolean(t) && arr.indexOf(t) === i) as string[];
    if (candidates.length === 0) throw new AccountsApiError('Not authenticated', 401);

    const headers = new Headers(init.headers as HeadersInit | undefined);

    const hasBody = init.body !== undefined && init.body !== null;
    if (hasBody && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    } else if (!hasBody && headers.has('Content-Type')) {
      headers.delete('Content-Type');
    }

    try {
      let lastRes: Response | null = null;
      for (const token of candidates) {
        headers.set('Authorization', 'Bearer ' + token);
        lastRes = await fetch(baseUrl + path, {
          ...init,
          headers,
        });
        if (lastRes.status !== 401) return lastRes;
      }
      // If we only got 401s, return the last one so the caller can handle/parse it.
      return lastRes as Response;
    } catch {
      throw new AccountsApiError('Network error', 0);
    }
  };

  let res = await execute();

  if (res.status === 401) {
    try {
      const refreshedSession = await fetchAuthSession({ forceRefresh: true });
      res = await execute(refreshedSession);
    } catch {
      throw new AccountsApiError('Authentication session unavailable', 401);
    }
  }

  const contentType = res.headers.get('content-type')?.toLowerCase() ?? '';
  const text = await res.text();
  const trimmed = text.trim();

  if (!res.ok) {
    let parsed: unknown = undefined;
    if (trimmed && (contentType.includes('json') || trimmed.startsWith('{') || trimmed.startsWith('['))) {
      try { parsed = JSON.parse(trimmed); } catch { /* noop */ }
    }
    const parsedObject = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    const detail = typeof parsedObject?.detail === 'string' ? parsedObject.detail : undefined;
    throw new AccountsApiError(detail ? `accounts API ${res.status}: ${detail}` : `accounts API ${res.status}`, res.status, parsed);
  }

  if (trimmed.length === 0) return undefined as T;

  if (contentType.includes('json') || (!contentType && (trimmed.startsWith('{') || trimmed.startsWith('[')))) {
    try { return JSON.parse(trimmed) as T; }
    catch { throw new AccountsApiError('Invalid JSON response from accounts API', res.status); }
  }

  return trimmed as unknown as T;
}
export type MeResponse = {
  id: string;
  email: string;
  email_verified: boolean;
  username?: string;
  entitlement?: {
    planKey?: string;
    supporterActive?: boolean;
    alertAllowance?: number;
  };
  entitlements?: {
    planKey?: string;
    supporterActive?: boolean;
    alertAllowance?: number;
  };
  subscription?: {
    tierKey?: string;
    active?: boolean;
    status?: string;
  };
  favorite_count?: number;
  distinct_view_count?: number;
  report_count?: number;
};

export type UsernameAvailabilityResponse = {
  username: string;
  available: boolean;
};

export async function checkUsernameAvailability(username: string): Promise<UsernameAvailabilityResponse> {
  const rawBaseUrl = process.env.NEXT_PUBLIC_API_URL;
  if (!rawBaseUrl) {
    throw new AccountsApiError('Missing NEXT_PUBLIC_API_URL', 0);
  }
  let baseUrl = normalizeAccountsBaseUrl(rawBaseUrl);
  if (shouldUseDevAccountsProxy(baseUrl)) {
    baseUrl = '/dev-accounts';
  }

  const url = `${baseUrl}/username-availability?username=${encodeURIComponent(username)}`;
  const res = await fetch(url, { cache: 'no-store' });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
    throw new AccountsApiError(typeof detail === 'string' ? detail : `accounts API ${res.status}`, res.status, body);
  }
  return body as UsernameAvailabilityResponse;
}

export async function getEmailFromIdToken(): Promise<MeResponse | null> {
  try {
    const s = await fetchAuthSession();
    const token = s.tokens?.idToken?.toString();
    if (!token) return null;
    const seg = token.split('.')[1];
    if (!seg) return null;
    const norm = seg.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(norm));
    const email = json?.email as string | undefined;
    if (!email) return null;
    return {
      id: (json?.sub as string) || 'me',
      email,
      email_verified: Boolean(json?.email_verified),
      username: normalizePublicUsername(json?.preferred_username as string | undefined) || usernameFromEmail(email),
    };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.debug('Failed to get email from ID token:', message);
    return null;
  }
}
